import React, { useEffect } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useSidebar } from "../../content/SidebarContent";
import PageHeader from "../../components/PageHeader";
import {
  reqToEditPlan,
  reqToGetPlanDetails,
  reqToGetPlanFeatureList,
} from "../../redux/slices.js/plans/plansSlice";
import { routes } from "../../helpers/constants";
import { CheckTrueIcon } from "../../assets/icons";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Plan Name is required!"),
  price: Yup.number().required("Price is required!").min(0, "Must be positive"),
  duration_in_days: Yup.number().required("Duration is required!"),
  max_active_users: Yup.number().required("Max users is required!"),
  status: Yup.string().required("Status is required!"),
  features: Yup.array().min(1, "Please select at least one feature."),
});

const EditPlan = () => {
  const { setIsSidebarOpen } = useSidebar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const plan = useSelector((state) => state.plans.planDetails);
  const planFeatures = useSelector((state) => state.plans.planFeatures);

  useEffect(() => {
    if (!plan || plan.id !== id) {
      dispatch(
        reqToGetPlanDetails({
          id,
          onSuccess: () => {},
          onFailure: () => {},
        })
      );
    }
    dispatch(
      reqToGetPlanFeatureList({
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [id]);

  const initialValues = {
    name: plan?.name || "",
    price: plan?.price || 0,
    duration_in_days: plan?.duration_in_days || "",
    max_active_users: plan?.max_active_users || "",
    status: plan?.is_active ? "active" : "inactive",
    // features: plan?.features || [],
    features: plan?.features?.map((r) => r) || [],
    pricePerUser: plan?.price_per_user || 0,
  };

  const handleSubmit = (values) => {
    const reqObj = {
      name: values?.name || "",
      price: values?.price || 0,
      duration_in_days: values?.duration_in_days || "",
      max_active_users: values?.max_active_users || "",
      is_active: values?.status === "active" ? true : false,
      features: values?.features || [],
      price_per_user: values?.pricePerUser || 0,
    };
    dispatch(
      reqToEditPlan({
        id,
        data: reqObj,
        onSuccess: () => navigate(routes.plan),
        onFailure: () => {},
      })
    );
  };

  return (
    <section className="dashboard-page main-layout-page main-layout h-[calc(100vh-50px)]">
      <div className="!p-0">
        <div className="main-layout-wrapper bg-white h-screen flex flex-col sticky z-1 top-0">
          <PageHeader
            title="Edit Plan"
            description="Update your plan details here."
            onOpenSidebar={() => setIsSidebarOpen(true)}
          />

          <div className="flex items-baseline gap-[40px] main-sidebar-wrapper flex-1">
            <div className="flex-1 right-sidebar h-full relative">
              <div className="right-sidebar-contend py-6 px-3 md:px-6 h-full">
                <Formik
                  initialValues={initialValues}
                  enableReinitialize
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue }) => (
                    <Form className="signin-form flex flex-col gap-6">
                      <div className="bg-white box-shadow p-5 space-y-7 rounded-md">
                        <h2 className="text-h4 text-blue">Plan Information</h2>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="name">
                              Plan Name<span>*</span>
                            </label>
                            <Field
                              name="name"
                              placeholder="Enter plan name"
                              className="w-full"
                            />
                            <ErrorMessage
                              name="name"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>

                          <div>
                            <label htmlFor="price">
                              Price ($)<span>*</span>
                            </label>
                            <Field
                              name="price"
                              type="number"
                              placeholder="Enter price"
                              className="w-full"
                            />
                            <ErrorMessage
                              name="price"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>
                          <div>
                            <label htmlFor="pricePerUser">
                              Price Per User($)<span>*</span>
                            </label>
                            <Field
                              name="pricePerUser"
                              type="number"
                              placeholder="Enter price"
                              className="w-full"
                            />
                            <ErrorMessage
                              name="pricePerUser"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>
                          <div>
                            <label htmlFor="duration_in_days">
                              Plan Duration (Days)<span>*</span>
                            </label>
                            <Field
                              name="duration_in_days"
                              type="number"
                              placeholder="Enter duration"
                              className="w-full"
                            />
                            <ErrorMessage
                              name="duration_in_days"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>

                          <div>
                            <label htmlFor="max_active_users">
                              Max Allowed Users<span>*</span>
                            </label>
                            <Field
                              name="max_active_users"
                              type="number"
                              placeholder="Enter user limit"
                              className="w-full"
                            />
                            <ErrorMessage
                              name="max_active_users"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>
                          <div>
                            <label htmlFor="status">
                              Status<span>*</span>
                            </label>
                            <Field as="select" name="status" className="w-full">
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </Field>
                            <ErrorMessage
                              name="status"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>
                        </div>

                        <div>
                          <label>
                            Features<span>*</span>
                          </label>
                          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3 mt-6">
                            <FieldArray name="features">
                              {({ push, remove, form }) => (
                                <>
                                  {planFeatures?.map((feature) => {
                                    const isChecked =
                                      form.values.features.includes(feature.id);

                                    return (
                                      <div
                                        key={feature.id}
                                        className="flex items-center gap-2 checkbox-wrapper"
                                      >
                                        <label className="flex items-center gap-2 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            className="peer hidden"
                                            checked={isChecked}
                                            onChange={() => {
                                              if (isChecked) {
                                                const idx =
                                                  form.values.features.indexOf(
                                                    feature.id
                                                  );
                                                remove(idx);
                                              } else {
                                                push(feature.id);
                                              }
                                            }}
                                          />
                                          <div className="w-[30px] flex-none h-[30px] border-[1px] border-[#1B3C5266] rounded-md flex items-center justify-center transition">
                                            {isChecked && CheckTrueIcon}
                                          </div>
                                        </label>
                                        <span className="text-span !font-medium text-black">
                                          {feature.name}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                            </FieldArray>
                          </div>

                          <div className="text-red-500 text-sm mt-1">
                            <ErrorMessage name="features" />
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4 mt-auto">
                        <button type="submit" className="login-btn text-white">
                          Update Plan
                        </button>
                        <button
                          type="button"
                          className="skip-btn border-blue"
                          onClick={()=>{navigate(-1)}}
                        >
                          Back
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditPlan;
